import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { CareerGoalsPreferences, VALID_LOCATIONS } from '../types';

const MAX_CHARS = 250;

const ICCareerGoalsSection: React.FC = () => {
  const [formData, setFormData] = useState<Omit<CareerGoalsPreferences, 'id' | 'alias'>>({
    location_preference_1: null,
    location_preference_2: null,
    location_preference_3: null,
    location_preference_notes: null,
    team_or_technology_alignment_interest: null,
    career_goals: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCareerGoals();
  }, []);

  const fetchCareerGoals = async () => {
    try {
      const data = await api.getCareerGoals();
      setFormData({
        location_preference_1: data.location_preference_1,
        location_preference_2: data.location_preference_2,
        location_preference_3: data.location_preference_3,
        location_preference_notes: data.location_preference_notes,
        team_or_technology_alignment_interest: data.team_or_technology_alignment_interest,
        career_goals: data.career_goals,
      });
    } catch (err: any) {
      if (err.message?.includes('404')) {
        // No existing record — that's fine, use empty form
      } else {
        console.error('Error fetching career goals:', err);
        setError('Error loading career goals data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (field: keyof typeof formData) => (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value || null }));
    setSaveSuccess(false);
  };

  const handleTextChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= MAX_CHARS) {
      setFormData((prev) => ({ ...prev, [field]: value || null }));
      setSaveSuccess(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await api.saveCareerGoals(formData);
      setSaveSuccess(true);
    } catch (err) {
      console.error('Error saving career goals:', err);
      setError('Error saving career goals. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Career Goals & Preferences
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading career goals...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h2">Career Goals & Preferences</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          size="small"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Career goals saved successfully.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Location Preference 1</InputLabel>
            <Select
              value={formData.location_preference_1 || ''}
              label="Location Preference 1"
              onChange={handleSelectChange('location_preference_1')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {VALID_LOCATIONS.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Location Preference 2</InputLabel>
            <Select
              value={formData.location_preference_2 || ''}
              label="Location Preference 2"
              onChange={handleSelectChange('location_preference_2')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {VALID_LOCATIONS.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Location Preference 3</InputLabel>
            <Select
              value={formData.location_preference_3 || ''}
              label="Location Preference 3"
              onChange={handleSelectChange('location_preference_3')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {VALID_LOCATIONS.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Location Preference Notes"
          value={formData.location_preference_notes || ''}
          onChange={handleTextChange('location_preference_notes')}
          helperText={`${(formData.location_preference_notes || '').length}/${MAX_CHARS} characters`}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Team or Technology Alignment Interest"
          value={formData.team_or_technology_alignment_interest || ''}
          onChange={handleTextChange('team_or_technology_alignment_interest')}
          helperText={`${(formData.team_or_technology_alignment_interest || '').length}/${MAX_CHARS} characters`}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Career Goals"
          value={formData.career_goals || ''}
          onChange={handleTextChange('career_goals')}
          helperText={`${(formData.career_goals || '').length}/${MAX_CHARS} characters`}
        />
      </Box>
    </Paper>
  );
};

export default ICCareerGoalsSection;
